<ng-container *transloco="let t">
<!-- Overlay Spinner -->
<div
    *ngIf="isGeneratingPDF()"
    class="fixed inset-0 flex items-center justify-center bg-black/50 z-50 no-pdf"
>
    <div class="spinner"></div>
</div>

<!-- Report Content -->
<ng-container *ngIf="report() as r">
    <div class="pdf-content relative w-full overflow-x-hidden bg-gray-100">
        <!-- Sticky Header -->
        <header
            class="sticky top-0 h-22 w-full z-10 bg-color print:bg-white print:static"
        >
            <div class="wrap row-between h-full">
                <div
                    class="text-gray-200 text-sm md:text-lg w-1/3 print:text-black"
                >
                    {{ t("Report ID") }}: #{{ reportId() }}
                </div>

                <div class="w-1/3 flex justify-center">
                    <div class="logo">
                        <img
                            class="rounded-full h-full w-full object-cover"
                            [authSrc]="
                                companyImage() ||
                                '../../../../../../../assets/images/report.png'
                            "
                            [alt]="t('Company Logo')"
                        />
                    </div>
                </div>

                <div
                    class="text-gray-200 text-end text-sm md:text-lg flex flex-col gap-1 w-1/3 print:text-black"
                >
                    <span>{{ r.created | date : "mediumDate" }}</span>
                    <span>{{ r.created | date : "shortTime" }}</span>
                </div>
            </div>
        </header>

        <div class="bg-white w-full mx-auto px-5 pt-10">
            <!-- Title -->
            <div class="wrap mt-12">
                <ng-container *ngIf="isEditMode(); else titleView">
                    <mat-form-field
                        class="w-full relative fuse-mat-dense"
                        subscriptSizing="dynamic"
                    >
                        <mat-label>{{ t("Report Title") }}</mat-label>
                        <input matInput type="text" [formControl]="titleCtrl" />
                    </mat-form-field>
                </ng-container>
                <ng-template #titleView>
                    <h1 class="title marked-styling" [innerHTML]="markDownContent(r.title)"></h1>
                </ng-template>
            </div>

            <!-- Introduction -->
            <section class="wrap mt-6 bg-white">
                <ng-container *ngIf="isEditMode(); else introView">
                    <mat-form-field class="w-full fuse-mat-dense" subscriptSizing="dynamic">
                        <mat-label>{{ t("Introduction") }}</mat-label>
                        <textarea
                            matInput
                            [formControl]="introductionCtrl"
                            maxlength="2000"
                            rows="3"
                            cdkTextareaAutosize
                            [placeholder]="t('Write a brief introduction for your report.')"
                        ></textarea>
                        <mat-hint align="end"
                            >{{
                                introductionCtrl.value?.length || 0
                            }}/2000</mat-hint
                        >
                    </mat-form-field>
                </ng-container>
                <ng-template #introView>
                    <h2 class="section-h mb-3">{{ t("Introduction") }}</h2>
                    <div class="section-p marked-styling" [innerHTML]="markDownContent(r.introduction)"></div>
                </ng-template>
            </section>

            <!-- DATA: arrows row → chart → matching table -->
            <section
                *ngIf="
                    isNewReport() &&
                    (r.chart_data?.length || r.data_table?.length)
                "
                class="wrap mt-10"
            >
                <h2 class="section-h mb-3">{{ t("Data") }}</h2>

                <!-- arrows + title -->
                <div class="data-header row-between no-pdf">
                    <button
                        mat-icon-button
                        *ngIf="hasMultipleCharts()"
                        (click)="prevChart()"
                        [disabled]="currentChartIndex() === 0"
                        class="nav-btn"
                        [attr.aria-label]="t('Previous query')"
                    >
                        <mat-icon>arrow_left</mat-icon>
                    </button>

                    <ng-container *ngIf="isEditMode(); else readOnlyTitle">
                        <mat-form-field class="w-full relative overflow-hidden fuse-mat-dense">
                            <mat-label>{{ t("Chart Title") }}</mat-label>
                            <input
                                matInput
                                type="text"
                                [formControl]="chartCtrl"
                            />
                            <button
                                mat-flat-button
                                matSuffix
                                class="save-btn"
                                (click)="saveNewTitle()"
                            >
                                {{ t("Save") }}
                            </button>
                        </mat-form-field>
                    </ng-container>
                    <ng-template #readOnlyTitle>
                        <div class="data-title">
                            {{ getStepTitle(currentStep()) }}
                        </div>
                    </ng-template>

                    <button
                        mat-icon-button
                        *ngIf="hasMultipleCharts()"
                        (click)="nextChart()"
                        [disabled]="
                            currentChartIndex() === r.chart_data.length - 1
                        "
                        class="nav-btn"
                        [attr.aria-label]="t('Next query')"
                    >
                        <mat-icon>arrow_right</mat-icon>
                    </button>
                </div>
                <!-- Applied filter/date info and errors (report context) -->
                <div class="row-between items-center mt-2 no-pdf">
                    <div class="flex gap-2 items-center">
                        <ng-container
                            *ngIf="
                                currentAppliedStartDate() ||
                                currentAppliedEndDate()
                            "
                        >
                            <div
                                class="m-1 py-0.5 px-3 rounded-full text-xs sm:text-sm font-medium bg-secondary line-clamp-1 max-h-6 sm:max-h-6 overflow-hidden"
                            >
                                {{ t("From") }}: {{ currentAppliedStartDate() || "-" }}
                            </div>
                            <div
                                class="m-1 py-0.5 px-3 rounded-full text-xs sm:text-sm font-medium bg-secondary line-clamp-1 max-h-6 sm:max-h-6 overflow-hidden"
                            >
                                {{ t("To") }}: {{ currentAppliedEndDate() || "-" }}
                            </div>
                        </ng-container>
                    </div>

                    <div class="flex-1 text-center">
                        <p
                            *ngIf="currentInvalidMessage()"
                            class="pt-2 text-center px-4 text-red-500 font-semibold"
                        >
                            {{ currentInvalidMessage() }}
                        </p>
                    </div>
                </div>
                <!-- CHART(S): one step per query - multiple charts stacked; table-only → placeholder -->
                <div class="chart-container">
                    <ng-container *ngIf="currentStep() as step">
                        <ng-container
                            *ngIf="
                                isReportQueryStep(step);
                                else legacyReportCharts
                            "
                        >
                            <ng-container *ngIf="showChart(); else chartLoadingTpl">
                                <ng-container
                                    *ngIf="
                                        step.chartsList?.length;
                                        else chartNotFoundTpl
                                    "
                                >
                                    <div
                                        *ngFor="
                                            let ch of step.chartsList;
                                            let ci = index
                                        "
                                        class="flex flex-col w-full gap-2 mb-8 last:mb-0"
                                    >
                                        <h3
                                            *ngIf="chartRenderable(ch)"
                                            class="w-full text-center text-xl md:text-2xl font-semibold text-gray-600 leading-snug break-words mb-2"
                                        >
                                            {{ getChartDisplayTitle(ch) }}
                                        </h3>
                                        <app-dynamic-charts
                                            *ngIf="chartRenderable(ch)"
                                            [chartData]="ch"
                                            [chartTitle]="
                                                getChartTitleForDynamic(ch)
                                            "
                                            [hideTitle]="true"
                                            [initDelayMs]="ci * 75"
                                            [from]="'report'"
                                            [isEditMode]="isEditMode()"
                                            [secondLevelFrom]="
                                                secondLevelFrom()
                                            "
                                            (filterChartEvent)="
                                                onFilterChart($event)
                                            "
                                            (dateRangeChange)="
                                                onReportDateRange($event)
                                            "
                                            (openTrackerDataTable)="
                                                openReportDataTable($event)
                                            "
                                        >
                                        </app-dynamic-charts>
                                        <div
                                            *ngIf="!chartRenderable(ch)"
                                            class="flex flex-col items-center justify-center min-h-48 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg px-4 py-8"
                                        >
                                            <mat-icon
                                                class="text-5xl mb-3 text-gray-400"
                                                >insert_chart</mat-icon
                                            >
                                            <p class="text-base font-medium">
                                                {{ t("Chart data unavailable for this visualization.") }}
                                            </p>
                                        </div>
                                    </div>
                                </ng-container>
                                <ng-template #chartNotFoundTpl>
                                    <div
                                        class="flex flex-col items-center justify-center h-96 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg px-4"
                                    >
                                        <mat-icon
                                            class="text-6xl mb-4 text-gray-400"
                                            >insert_chart_outlined</mat-icon
                                        >
                                        <p class="text-lg font-medium">
                                            {{ t("Chart not found") }}
                                        </p>
                                        <p
                                            class="text-sm text-gray-400 mt-2 text-center max-w-md"
                                        >
                                            {{ t("No chart was generated for this query. Related table data is shown below.") }}
                                        </p>
                                    </div>
                                </ng-template>
                            </ng-container>
                            <ng-template #chartLoadingTpl>
                                <div
                                    class="flex items-center justify-center h-96 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg"
                                >
                                    <div class="text-center">
                                        <mat-icon
                                            class="text-6xl mb-4 text-gray-400 animate-pulse"
                                            >insert_chart</mat-icon
                                        >
                                        <p class="text-sm text-gray-400">
                                            {{ t("Loading chart...") }}
                                        </p>
                                    </div>
                                </div>
                            </ng-template>
                        </ng-container>
                        <ng-template #legacyReportCharts>
                            <app-dynamic-charts
                                *ngIf="
                                    showChart() &&
                                    hasValidChartData() &&
                                    step as chart
                                "
                                [chartData]="chart"
                                [chartTitle]="''"
                                [from]="'report'"
                                [isEditMode]="isEditMode()"
                                [secondLevelFrom]="secondLevelFrom()"
                                (filterChartEvent)="onFilterChart($event)"
                                (dateRangeChange)="onReportDateRange($event)"
                                (openTrackerDataTable)="
                                    openReportDataTable($event)
                                "
                            >
                            </app-dynamic-charts>
                            <div
                                *ngIf="!showChart() || !hasValidChartData()"
                                class="flex items-center justify-center h-96 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg"
                            >
                                <div class="text-center">
                                    <mat-icon
                                        class="text-6xl mb-4 text-gray-400"
                                        >insert_chart</mat-icon
                                    >
                                    <p
                                        class="text-lg font-medium"
                                        *ngIf="
                                            !hasValidChartData() && currentStep()
                                        "
                                    >
                                        {{ t("There is no chart data") }}
                                    </p>
                                    <p
                                        class="text-lg font-medium"
                                        *ngIf="!currentStep()"
                                    >
                                        {{ t("No chart data available") }}
                                    </p>
                                    <p
                                        class="text-sm text-gray-400 mt-2"
                                        *ngIf="
                                            !showChart() && hasValidChartData()
                                        "
                                    >
                                        {{ t("Loading chart...") }}
                                    </p>
                                </div>
                            </div>
                        </ng-template>
                    </ng-container>
                </div>

                <!-- meta row -->
                <div class="row-between items-center mt-16 md:mt-4">
                    <p
                        class="text-start text-base md:text-lg font-normal text-gray-500 leading-4 flex items-center gap-2"
                    >
                        <span
                            class="text-lg md:text-xl font-semibold text-gray-600 leading-4"
                            >{{ t("Source Table") }}:
                        </span>
                        <span>{{
                            r.tablesName[currentChartIndex()] || "-"
                        }}</span>

                        <!-- NEW: Expand/Collapse toggle for the current table -->
                        <button
                            *ngIf="currentTableLength() > 10; else shortHint"
                            type="button"
                            class="icon-chip no-pdf"
                            mat-icon-button
                            (click)="toggleNewTable(currentChartIndex())"
                            [matTooltip]="
                                expandedNewTables()[currentChartIndex()] ||
                                false
                                    ? 'Collapse table'
                                    : 'Expand table'
                            "
                        >
                            <mat-icon>{{
                                expandedNewTables()[currentChartIndex()] ||
                                false
                                    ? "unfold_less"
                                    : "unfold_more"
                            }}</mat-icon>
                        </button>
                        <ng-template #shortHint></ng-template>
                    </p>

                    <button
                        *ngIf="isEditMode()"
                        mat-icon-button
                        class="sai-card-action-btn no-pdf"
                        (click)="deleteChartAt(currentChartIndex())"
                        [attr.aria-label]="t('Delete chart')"
                    >
                        <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                    </button>
                </div>

                <!-- TABLE for current query step -->
                <ng-container *ngIf="currentTable() as tableData">
                    <p
                        *ngIf="!tableData?.length"
                        class="mt-3 text-gray-500 text-center"
                    >
                        {{ t("No table rows for this query.") }}
                    </p>
                    <div
                        *ngIf="tableData?.length"
                        class="table-shell mt-3 collapsible-table"
                        [class.expanded]="
                            expandedNewTables()[currentChartIndex()] || false
                        "
                        [style.--collapsed-h]="
                            getCollapsedHeight(currentChartIndex()) + 'px'
                        "
                    >
                        <div
                            class="table-container"
                            [class.expanded]="
                                expandedNewTables()[currentChartIndex()] ||
                                false
                            "
                            [class.collapsed]="
                                !(
                                    expandedNewTables()[currentChartIndex()] ||
                                    false
                                )
                            "
                        >
                            <table class="grid-table">
                                <thead class="sticky-header">
                                    <tr>
                                        <th
                                            *ngFor="
                                                let key of keysOf(tableData[0]);
                                                trackBy: trackByIndex
                                            "
                                        >
                                            {{ key | titlecase }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <ng-container
                                        *ngIf="
                                            expandedNewTables()[
                                                currentChartIndex()
                                            ] || false
                                        "
                                    >
                                        <tr
                                            *ngFor="
                                                let row of tableData;
                                                trackBy: trackByIndex
                                            "
                                        >
                                            <td
                                                *ngFor="
                                                    let key of keysOf(
                                                        tableData[0]
                                                    );
                                                    trackBy: trackByIndex
                                                "
                                                [appCopyOnDblclick]="cell"
                                                class="copy-cell hover:bg-gray-200"
                                            >
                                                {{
                                                    row[key] | formatting : key
                                                }}
                                            </td>
                                        </tr>
                                    </ng-container>

                                    <ng-container
                                        *ngIf="
                                            !(
                                                expandedNewTables()[
                                                    currentChartIndex()
                                                ] || false
                                            )
                                        "
                                    >
                                        <tr
                                            *ngFor="
                                                let row of tableData
                                                    | slice : 0 : 10;
                                                trackBy: trackByIndex
                                            "
                                        >
                                            <td
                                                *ngFor="
                                                    let key of keysOf(
                                                        tableData[0]
                                                    );
                                                    trackBy: trackByIndex
                                                "
                                                [appCopyOnDblclick]="cell"
                                                class="copy-cell hover:bg-gray-200"
                                            >
                                                {{
                                                    row[key] | formatting : key
                                                }}
                                            </td>
                                        </tr>
                                    </ng-container>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </ng-container>
            </section>

            <!-- Legacy charts/tables -->
            <section
                *ngIf="!isNewReport() && r.chart_data?.length"
                class="wrap mt-6"
            >
                <h2 class="section-h mb-2">{{ t("Data Charts") }}</h2>
                <app-dynamic-charts
                    [chartData]="r.chart_data"
                    [chartTitle]="getChartTitle(r.chart_data)"
                ></app-dynamic-charts>
            </section>

            <section
                *ngIf="!isNewReport() && r.data_table?.length"
                class="wrap mt-6"
            >
                <h2 class="section-h mb-2">{{ t("Data Tables") }}</h2>
                <!-- Example for first legacy table toggle (extend to *ngFor if multiple) -->
                <div
                    class="table-shell mt-3 collapsible-table"
                    [class.expanded]="isLegacyTableExpanded(0)"
                    [style.--collapsed-h]="
                        getCollapsedHeight(0, 'legacy') + 'px'
                    "
                >
                    <div class="row-between items-center px-3 py-2 no-pdf">
                        <div
                            class="text-base md:text-lg font-semibold text-gray-700"
                        >
                            {{ t("Table 1") }}
                        </div>
                        <button
                            type="button"
                            class="icon-chip"
                            mat-icon-button
                            (click)="toggleLegacyTable(0)"
                            [matTooltip]="
                                isLegacyTableExpanded(0)
                                    ? 'Collapse table'
                                    : 'Expand table'
                            "
                        >
                            <mat-icon>{{
                                isLegacyTableExpanded(0)
                                    ? "unfold_less"
                                    : "unfold_more"
                            }}</mat-icon>
                        </button>
                    </div>

                    <table class="grid-table">
                        <thead class="sticky-header">
                            <tr>
                                <th
                                    *ngFor="let key of keysOf(r.data_table[0]?.[0]); trackBy: trackByIndex"
                                >
                                    {{ key | titlecase }}
                                </th>
                            </tr>
                        </thead>
                    </table>

                    <ng-container
                        *ngIf="isLegacyTableExpanded(0); else legacyCollapsed"
                    >
                        <div class="table-full">
                            <table class="grid-table">
                                <tbody>
                                    <tr
                                        *ngFor="
                                            let item of r.data_table[0];
                                            trackBy: trackByIndex
                                        "
                                    >
                                        <td
                                            *ngFor="let key of keysOf(r.data_table[0]?.[0]); trackBy: trackByIndex"
                                        >
                                            {{ item[key] | formatting : key }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </ng-container>

                    <ng-template #legacyCollapsed>
                        <div class="table-scroll">
                            <table class="grid-table">
                                <tbody>
                                    <tr
                                        *ngFor="
                                            let item of r.data_table[0];
                                            trackBy: trackByIndex
                                        "
                                    >
                                        <td
                                            *ngFor="let key of keysOf(r.data_table[0]?.[0]); trackBy: trackByIndex"
                                        >
                                            {{ item[key] | formatting : key }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </ng-template>
                </div>
            </section>

            <!-- Analysis -->
            <section class="wrap mt-6">
                <ng-container *ngIf="isEditMode(); else analysisView">
                    <mat-form-field class="w-full fuse-mat-dense" subscriptSizing="dynamic">
                        <mat-label>{{ t("Data Analysis") }}</mat-label>
                        <textarea
                            matInput
                            [formControl]="analysisCtrl"
                            maxlength="2000"
                            rows="5"
                            cdkTextareaAutosize
                            [placeholder]="t('Write a brief analysis for your report.')"
                        ></textarea>
                        <mat-hint align="end"
                            >{{
                                analysisCtrl.value?.length || 0
                            }}/2000</mat-hint
                        >
                    </mat-form-field>
                </ng-container>
                <ng-template #analysisView>
                    <h2 class="section-h my-3">{{ t("Data Analysis") }}</h2>
                    <div class="section-p marked-styling" [innerHTML]="markDownContent(r.analysis)"></div>
                </ng-template>
            </section>
            <mat-divider class="my-6"></mat-divider>

            <!-- Insights -->
            <section class="wrap mt-6">
                <ng-container *ngIf="isEditMode(); else insightsView">
                    <mat-form-field class="w-full fuse-mat-dense" subscriptSizing="dynamic">
                        <mat-label>{{ t("Insights and Interpretation") }}</mat-label>
                        <textarea
                            matInput
                            [formControl]="insightsCtrl"
                            maxlength="2000"
                            rows="5"
                            cdkTextareaAutosize
                            [placeholder]="t('Write brief insights for your report.')"
                        ></textarea>
                        <mat-hint align="end"
                            >{{
                                insightsCtrl.value?.length || 0
                            }}/2000</mat-hint
                        >
                    </mat-form-field>
                </ng-container>
                <ng-template #insightsView>
                    <h2 class="section-h my-3">{{ t("Insights and Interpretation") }}</h2>
                    <div class="section-p marked-styling" [innerHTML]="markDownContent(r.insights)"></div>
                </ng-template>
            </section>
            <mat-divider class="my-6"></mat-divider>

            <!-- Conclusion -->
            <section class="wrap mt-6 mb-36">
                <ng-container *ngIf="isEditMode(); else conclusionView">
                    <mat-form-field class="w-full fuse-mat-dense" subscriptSizing="dynamic">
                        <mat-label>{{ t("Conclusion and Recommendations") }}</mat-label>
                        <textarea
                            matInput
                            [formControl]="conclusionCtrl"
                            maxlength="2000"
                            rows="5"
                            cdkTextareaAutosize
                            [placeholder]="t('Write a brief conclusion for your report.')"
                        ></textarea>
                        <mat-hint align="end"
                            >{{
                                conclusionCtrl.value?.length || 0
                            }}/2000</mat-hint
                        >
                    </mat-form-field>
                </ng-container>
                <ng-template #conclusionView>
                    <h2 class="section-h my-3">
                        {{ t("Conclusion and Recommendations") }}
                    </h2>
                    <div class="section-p marked-styling" [innerHTML]="markDownContent(r.conclusion)"></div>
                </ng-template>
            </section>

            <!-- Generated by footer (sticky in app, hidden in print) -->
            <div
                class="hidden sm:flex sticky bottom-0 items-center h-16 justify-center w-full border-t border-gray-300 no-pdf"
                [ngClass]="isEditMode() ? 'mb-2' : 'mb-16'"
            >
                <span
                    class="text-sm md:text-base lg:text-lg inline-block ms-1 xl:ms-4"
                >
                    {{ t("This Report Has Been Generated By SAI.") }}
                </span>
            </div>
        </div>

        <!-- Actions -->
        <div
            *ngIf="isEditMode(); else sendEmailTpl"
            class="fixed bottom-2 right-8 z-10 flex flex-col gap-4 w-fit py-6 no-pdf"
        >
            <button (click)="editReport()" class="action-btn">
                <mat-icon
                    class="icon-size-3 md:icon-size-3 text-gray-100 mt-1"
                    svgIcon="heroicons_outline:pencil"
                ></mat-icon>
                <span class="text-gray-50 pe-2">{{ t("Save Changes") }}</span>
            </button>
        </div>

        <ng-template #sendEmailTpl>
            <div
                class="fixed bottom-16 right-8 z-10 flex flex-col gap-4 w-fit py-1 no-pdf"
            >
                <button
                    *ngIf="hasReportData()"
                    type="button"
                    (click)="showDatatable()"
                    class="send-btn"
                    [matTooltip]="'View Data' | transloco"
                >
                    <mat-icon
                        class="icon-size-4 md:icon-size-6 text-gray-100 mt-1"
                        >table_view</mat-icon
                    >
                </button>
                <button (click)="sendEmail()" class="send-btn">
                    <mat-icon
                        class="icon-size-4 md:icon-size-6 text-gray-100 mt-1"
                        svgIcon="heroicons_outline:paper-airplane"
                    ></mat-icon>
                </button>
            </div>
        </ng-template>

        <!-- Powered by footer (app only) -->
        <footer
            *ngIf="!isEditMode()"
            class="fixed bottom-0 h-16 flex items-center justify-center w-full z-10 bg-color text-center text-gray-50 no-pdf"
        >
            <span class="text-sm md:text-base lg:text-lg"
                >{{ t("Powered By Nuwatt Technovation 2025.") }}</span
            >
        </footer>
    </div>
</ng-container>
</ng-container>
